import { Link } from 'react-router-dom';
import { PiArrowRightBold } from 'react-icons/pi';
import { useI18n } from '@v2/providers/I18nProvider';
import type { SellerCommissionForecast } from '@v2/pages/commissioning/types';
import { fmtBrl, fmtBrlShort, monthLabelCap } from './homeFormat';

type Props = {
    forecast: SellerCommissionForecast | null | undefined;
    onShowFull?: () => void;
    historyBase: string;
};

export default function SellerNextReceipts({ forecast, onShowFull, historyBase }: Props) {
    const { t, locale } = useI18n();
    const dateLocale = locale === 'pt_BR' ? 'pt-BR' : locale === 'zh_CN' ? 'zh-CN' : locale === 'es' ? 'es' : 'en-US';

    // Próximos 4 meses a partir do mês seguinte ao atual (índice 1..4 no horizon).
    const months = (forecast?.months ?? []).slice(1, 5);
    const total = months.reduce((s, m) => s + m.amount, 0);

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <h2 className="text-sm font-bold uppercase tracking-wide text-gray-800 dark:text-zinc-100">
                {t('v2.my_commission.next_receipts_title')}
            </h2>

            {months.length === 0 ? (
                <p className="py-6 text-center text-xs text-gray-400">{t('v2.my_commission.forecast_empty')}</p>
            ) : (
                <ul className="mt-3 divide-y divide-gray-100 dark:divide-zinc-800">
                    {months.map((m) => (
                        <li key={m.month}>
                            <Link
                                to={`${historyBase}/${m.month}`}
                                className="flex items-center justify-between gap-3 py-2.5 hover:bg-gray-50 dark:hover:bg-zinc-800/50 -mx-1 px-1 rounded-lg"
                            >
                                <div>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                        {monthLabelCap(m.month, dateLocale)}
                                    </p>
                                    <p className="text-[11px] text-gray-500">
                                        {t('v2.my_commission.forecast_month_count', {
                                            count: String(m.installments_count),
                                        })}
                                    </p>
                                </div>
                                <p className="text-sm font-bold tabular-nums text-orange-700 dark:text-orange-300">
                                    {fmtBrlShort(m.amount)}
                                </p>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}

            {months.length > 0 && (
                <p className="mt-2 border-t border-gray-100 pt-2 text-sm font-semibold text-gray-800 dark:border-zinc-800 dark:text-zinc-100">
                    {t('v2.my_commission.next_receipts_total', { amount: fmtBrl(total) })}
                </p>
            )}

            <button
                type="button"
                onClick={onShowFull}
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-orange-700 hover:underline dark:text-orange-400"
            >
                {t('v2.my_commission.next_receipts_full')}
                <PiArrowRightBold className="size-3.5" />
            </button>
        </section>
    );
}
